<script lang="ts" setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { v4 as uuid } from 'uuid'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

/**
 * NovelMello 搜索页视图层
 *
 * ⚠️ 纯展示组件：props / emits 契约与 SearchPage.default.vue、SearchPage.flipnovel.vue
 * 完全一致，不得增删。所有请求、分页、埋点、路由逻辑都在 src/pages/search/index.vue。
 */
const props = defineProps<{
  config: any
  input: string
  skeleton: boolean
  fetching: boolean
  ending: boolean
  error: any
  passcode: any
  result: any[]
  setInfiniteRef: (el: HTMLElement | null) => void
  onClear: () => void
  onSearch: () => void
  gotoBack: () => void
  gotoPasscodeBook: (passcode: any) => void
  onTrackGotoBook: (bookId: string) => boolean
}>()

const emit = defineEmits<{
  (event: 'update:input', value: string): void
}>()

const route = useRoute()
const router = useRouter()

const c = computed(() => props.config.colors)
const chipLimit = computed(() => props.config.chipLimit ?? 3)
// 占位符颜色（scoped ::placeholder 无法走内联 style，用 v-bind 注入）
const placeholderColor = computed(() => c.value.ink3)

// ---------------- 输入框（UI 状态，不影响搜索逻辑） ----------------
const inputRef = ref<HTMLInputElement>()
const focused = ref(false)

function onInput(event: Event) {
  emit('update:input', (event.target as HTMLInputElement).value)
}

const inputBoxStyle = computed(() => ({
  background: c.value.inputBg,
  borderRadius: c.value.inputRadius,
  height: c.value.inputHeight,
  border: `1.5px solid ${focused.value ? c.value.inputBorderFocus : c.value.inputBorder}`,
}))

// 与 flipnovel 版一致：带 kw 进来时不抢焦点，直接展示结果
onMounted(async () => {
  if (route.query.autofocus === '1' || !route.query.kw) {
    await nextTick()
    inputRef.value?.focus()
  }
})

// ---------------- 关键词高亮（纯渲染，无副作用） ----------------
/** 转义正则元字符，避免用户输入 `(`、`*` 等导致 RegExp 抛错 */
function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * 把 text 按 keyword 切成 [{ text, hit }] 段，交给模板用 span 渲染。
 * 不用 v-html：接口文本直接进 DOM 有 XSS 风险。
 */
function splitByKeyword(text: string, keyword: string) {
  const source = text ?? ''
  const kw = (keyword ?? '').trim()
  if (!kw)
    return [{ text: source, hit: false }]

  const reg = new RegExp(`(${escapeRegExp(kw)})`, 'gi')
  return source
    .split(reg)
    .filter(seg => seg !== '')
    .map(seg => ({ text: seg, hit: seg.toLowerCase() === kw.toLowerCase() }))
}

function titleSegments(text: string) {
  return splitByKeyword(text, props.input)
}

// ---------------- chips ----------------
/** 状态 chip + tagList 前 N 个，粉紫交替 */
function chipsOf(item: any) {
  const status = item.finishStatus ? props.config.texts.completed : props.config.texts.ongoing
  const tags = (item.tagList ?? [])
    .slice(0, chipLimit.value)
    .map((t: any) => t?.name)
    .filter(Boolean)
  return [status, ...tags]
}

function chipStyle(index: number) {
  const pink = index % 2 === 0
  return {
    background: pink ? c.value.chipPinkBg : c.value.chipPurpleBg,
    color: pink ? c.value.chipPinkText : c.value.chipPurpleText,
  }
}

// ---------------- 跳转（仅转发 index.vue 传入的埋点方法） ----------------
function gotoBook(item: any) {
  // 保留「埋点返回 false 则不跳转」的短路语义
  if (!props.onTrackGotoBook(item.id))
    return

  router.push({
    name: READER_ROUTE_NAME,
    params: { contentId: item.id, chapterId: item.firstChapter?.id || '' },
  })
}
</script>

<template>
  <div
    class="nm-search-page h-100dvh overflow-hidden flex flex-col"
    :style="{ background: c.pageBg }"
  >
    <!-- 搜索行：padding 16 / 与 Cancel 间距 12 -->
    <div class="nm-search-toolbar px-16px py-16px">
      <div class="w-full flex items-center">
        <div class="relative flex-1 flex items-center" :style="inputBoxStyle">
          <i-assets-icon-search
            class="nm-search-icon absolute left-14px w-17px h-17px"
            :style="{ color: c.ink3 }"
          />
          <input
            ref="inputRef"
            :value="input"
            enterkeyhint="search"
            class="nm-search-input ml0! flex-1 w-full h-full bg-transparent border-0 !outline-0 truncate pl-39px pr-38px"
            :style="{ color: c.ink1, caretColor: c.inputBorderFocus }"
            :placeholder="config.texts.placeholder"
            autofocus
            @input="onInput"
            @focus="focused = true"
            @blur="focused = false"
            @keyup.enter="onSearch"
          >
          <div
            v-show="input.length"
            class="absolute right-12px flex items-center"
            @click="onClear"
          >
            <i-lucide-circle-x class="w-16px h-16px" :style="{ color: c.ink3 }" />
          </div>
        </div>
        <span
          class="ml-12px shrink-0 text-14px"
          :style="{ color: c.ink2 }"
          @click="gotoBack()"
        >
          {{ config.texts.cancel }}
        </span>
      </div>
    </div>

    <!-- 结果区 -->
    <div :ref="setInfiniteRef" class="nm-search-content flex-1 overflow-y-auto px-16px pt-4px pb-16px">
      <div v-if="skeleton">
        <div class="pt-20vh flex justify-center">
          <BasicSpinner class="mx-auto" />
        </div>
        <p class="mt-4 text-center text-14px font-bold" :style="{ color: c.ink2 }">
          {{ config.texts.loading }}
        </p>
      </div>

      <div v-else-if="error" class="mt-4">
        <BizBlockError :title="error.msg" />
      </div>

      <!-- 无结果态 / 未搜索初始态：沿用现有组件，文案色走 n-text-3 自动跟随主题 -->
      <div v-else-if="!passcode && !result.length">
        <BizBlockEmpty :title="config.texts.noResults" />
      </div>

      <template v-else>
        <!-- 兑换码命中 -->
        <div v-if="passcode" class="flex items-start">
          <img
            class="shrink-0 object-cover"
            :style="{ width: c.coverWidth, height: '88px', borderRadius: c.coverRadius }"
            :src="passcode.contentCoverUrl"
            alt="book"
          >
          <div class="ml-12px min-w-0 flex-1">
            <p class="text-14px font-800 line-clamp-1" :style="{ color: c.ink1 }">
              {{ passcode.contentName }}
            </p>
            <p class="nm-desc mt-4px text-12px line-clamp-2" :style="{ color: c.ink2 }">
              {{ passcode.introduce }}
            </p>
            <a
              class="inline-block mt-8px px-12px py-5px text-12px lh-17px text-white rounded-full"
              :style="{ background: c.startReadingBg }"
              @click="gotoPasscodeBook(passcode)"
            >
              {{ config.texts.startReading }}
            </a>
          </div>
        </div>

        <!-- 搜索结果列表 -->
        <div v-if="result.length">
          <div
            v-for="(item, idx) in result"
            :key="item.id"
            class="flex items-start"
            :class="(idx > 0 || passcode) ? 'mt-16px' : ''"
            @click="gotoBook(item)"
          >
            <BizBookCover
              :id="uuid()"
              :src="item.contentCoverUrl || ''"
              :height-rate="c.coverHeightRate"
              :extra="{
                author: item.authorInfo.author,
                ...item,
              }"
              class="shrink-0 overflow-hidden"
              :style="{ width: c.coverWidth, borderRadius: c.coverRadius }"
            />
            <div class="ml-12px min-w-0 flex-1">
              <!-- 标题：命中关键词紫色高亮 -->
              <p class="text-14px font-800 line-clamp-1" :style="{ color: c.ink1 }">
                <span
                  v-for="(seg, i) in titleSegments(item.contentName)"
                  :key="i"
                  :style="seg.hit ? { color: c.highlight } : undefined"
                >{{ seg.text }}</span>
              </p>

              <!-- 简介：12px / 1.45 / 两行截断 -->
              <p class="nm-desc mt-4px text-12px line-clamp-2" :style="{ color: c.ink2 }">
                {{ item.introduce }}
              </p>

              <!-- chips：粉紫 soft 交替 -->
              <div class="nm-chips mt-8px flex flex-wrap items-center">
                <span
                  v-for="(chip, ci) in chipsOf(item)"
                  :key="ci"
                  class="nm-chip px-8px py-3px text-10px font-700 rounded-8px"
                  :style="chipStyle(ci)"
                >{{ chip }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="ending" class="py-15px text-13px text-center" :style="{ color: c.ink3 }">
          {{ config.texts.noMore }}
        </div>
        <div
          v-else-if="fetching"
          class="py-15px flex items-center justify-center text-13px"
          :style="{ color: c.ink3 }"
        >
          <div class="animate-spin">
            <i-icon-park-outline-loading />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.nm-search-toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
}

.nm-search-content {
  min-height: 0;
}

.nm-search-icon {
  top: 50%;
  transform: translateY(-50%);
}

.nm-search-input {
  font-size: 13px;
  font-weight: 700;
}

/* brand-novelmello.scss 的全局 input 规则会给内层 input 再涂粉底/12px 圆角,
   focus 时还有外圈 box-shadow,叠在外层胶囊 wrapper 上形成双层框。
   本组件的输入框视觉全部由外层 wrapper(inputBoxStyle)承担,内层必须保持透明。 */
.nm-search-input,
.nm-search-input:focus {
  background-color: transparent !important;
  border-radius: 0 !important;
  border-color: transparent !important;
  box-shadow: none !important;
}

/* 占位符用 ink-3，且不加粗，避免占位文字过重 */
.nm-search-input::placeholder {
  font-weight: 400;
  color: v-bind(placeholderColor);
}

.nm-desc {
  line-height: 1.45;
}

/*
 * flex gap 兼容性：项目 scan:flex-gap 会拦截 class 里的 gap 写法，
 * chips 间距用 margin 实现（参考 scripts/scan-flex-gap.ts 规则）
 */
.nm-chips > .nm-chip {
  margin-right: 6px;
  margin-top: 4px;
}

.nm-chips > .nm-chip:last-child {
  margin-right: 0;
}
</style>
